import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Users, 
  TrendingUp, 
  Activity,
  Target,
  Flame,
  Trophy,
  ChevronRight,
  Plus,
  Scale,
  Ruler,
  FileText,
  Calendar,
  BarChart3,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Dumbbell
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line } from 'recharts';

export default function TrainerDashboardPage() {
  const { user, token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProgress, setClientProgress] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [progressForm, setProgressForm] = useState({
    weight_kg: '',
    body_fat_percentage: '',
    notes: '',
    chest: '',
    waist: '',
    hips: '',
    arms: '',
    thighs: ''
  });
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role === 'trainer' || user?.role === 'admin') {
      fetchDashboard();
      fetchClients();
    }
  }, [token, user]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientProgress(selectedClient.id);
      fetchClientNotes(selectedClient.id);
    }
  }, [selectedClient, token]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/trainer/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/trainer/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchClientProgress = async (clientId) => {
    try {
      const response = await axios.get(`${API}/trainer/client/${clientId}/progress?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientProgress(response.data);
    } catch (error) {
      console.error('Error fetching progress:', error);
    }
  };

  const fetchClientNotes = async (clientId) => {
    try {
      const response = await axios.get(`${API}/trainer/client/${clientId}/notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const logProgress = async () => {
    if (!selectedClient) return;
    setSubmitting(true);
    
    try {
      const measurements = {};
      if (progressForm.chest) measurements.chest = parseFloat(progressForm.chest);
      if (progressForm.waist) measurements.waist = parseFloat(progressForm.waist);
      if (progressForm.hips) measurements.hips = parseFloat(progressForm.hips);
      if (progressForm.arms) measurements.arms = parseFloat(progressForm.arms);
      if (progressForm.thighs) measurements.thighs = parseFloat(progressForm.thighs);

      const params = new URLSearchParams();
      if (progressForm.weight_kg) params.append('weight_kg', progressForm.weight_kg);
      if (progressForm.body_fat_percentage) params.append('body_fat_percentage', progressForm.body_fat_percentage);
      if (progressForm.notes) params.append('notes', progressForm.notes);

      await axios.post(
        `${API}/trainer/client/${selectedClient.id}/progress?${params}`,
        { measurements: Object.keys(measurements).length > 0 ? measurements : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Progress logged successfully!');
      setShowProgressDialog(false);
      setProgressForm({ weight_kg: '', body_fat_percentage: '', notes: '', chest: '', waist: '', hips: '', arms: '', thighs: '' });
      fetchClientProgress(selectedClient.id);
      fetchClients();
    } catch (error) {
      toast.error('Failed to log progress');
    } finally {
      setSubmitting(false);
    }
  };

  const addNote = async () => {
    if (!selectedClient || !newNote.trim()) return;
    setSubmitting(true);

    try {
      await axios.post(
        `${API}/trainer/client/${selectedClient.id}/note?note=${encodeURIComponent(newNote)}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Note added!');
      setShowNoteDialog(false);
      setNewNote('');
      fetchClientNotes(selectedClient.id);
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'trainer' && user?.role !== 'admin') {
    return (
      <div className="text-center py-12 card-tactical">
        <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-xl text-white mb-2">Trainer Access Required</h3>
        <p className="text-gray-400">This dashboard is only available to trainers.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const overview = dashboard?.overview || {};
  const weeklyStats = dashboard?.weekly_stats || {};

  return (
    <div data-testid="trainer-dashboard-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
          TRAINER <span className="text-primary">DASHBOARD</span>
        </h1>
        <p className="text-gray-400 mt-1">Monitor client progress and transformations</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-tactical">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider mb-1">Total Clients</div>
              <div className="font-mono text-4xl font-bold text-white">{overview.total_clients || 0}</div>
            </div>
            <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </div>
        </div>

        <div className="card-tactical">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider mb-1">Active This Week</div>
              <div className="font-mono text-4xl font-bold text-white">{overview.active_clients || 0}</div>
            </div>
            <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
              <Activity className="w-6 h-6 text-green-500" />
            </div>
          </div>
          <div className="mt-2 text-sm text-green-500">
            <ArrowUpRight className="w-4 h-4 inline" />
            {overview.total_clients > 0 ? Math.round((overview.active_clients / overview.total_clients) * 100) : 0}% active rate
          </div>
        </div>

        <div className="card-tactical">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider mb-1">Workouts This Week</div>
              <div className="font-mono text-4xl font-bold text-white">{weeklyStats.total_workouts || 0}</div>
            </div>
            <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
              <Dumbbell className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="card-tactical">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider mb-1">Active Subscriptions</div>
              <div className="font-mono text-4xl font-bold text-white">{overview.active_subscriptions || 0}</div>
            </div>
            <div className="w-12 h-12 bg-yellow-500/10 rounded-sm flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client List */}
        <div className="card-tactical lg:col-span-1">
          <h3 className="font-heading text-xl text-white uppercase mb-4">Clients</h3>
          <ScrollArea className="h-[500px]">
            <div className="space-y-2 pr-4">
              {clients.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No clients yet</p>
                </div>
              ) : (
                clients.map((client) => (
                  <button
                    key={client.id}
                    data-testid={`select-client-${client.id}`}
                    onClick={() => setSelectedClient(client)}
                    className={`w-full flex items-center gap-3 p-3 rounded-sm transition-all ${
                      selectedClient?.id === client.id
                        ? 'bg-primary/20 border border-primary'
                        : 'bg-secondary/30 hover:bg-secondary/50 border border-transparent'
                    }`}
                  >
                    <Avatar>
                      <AvatarFallback className="bg-primary/20 text-primary">
                        {client.first_name?.[0]}{client.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <div className="text-white font-medium">
                        {client.first_name} {client.last_name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Flame className="w-3 h-3 text-primary" />
                        {client.current_streak || 0} streak
                        <span className="text-gray-600">•</span>
                        {client.recent_activity?.total_workouts || 0} workouts/wk
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </button>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Client Details */}
        <div className="lg:col-span-2">
          {selectedClient ? (
            <div className="space-y-4">
              {/* Client Header */}
              <div className="card-tactical">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-16 h-16">
                      <AvatarFallback className="bg-primary/20 text-primary text-xl">
                        {selectedClient.first_name?.[0]}{selectedClient.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h2 className="font-heading text-2xl text-white">
                        {selectedClient.first_name} {selectedClient.last_name}
                      </h2>
                      <p className="text-gray-400">{selectedClient.email}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-gray-400">
                          Goal: <span className="text-white capitalize">{selectedClient.fitness_goal?.replace('_', ' ')}</span>
                        </span>
                        <span className="text-gray-400">
                          Level: <span className="text-white capitalize">{selectedClient.fitness_level}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
                      <DialogTrigger asChild>
                        <Button data-testid="log-progress-btn" className="bg-primary hover:bg-red-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Log Progress
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card border-border max-w-lg">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl text-white uppercase">
                            Log Client Progress
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-gray-300">Weight (kg)</Label>
                              <Input
                                type="number"
                                value={progressForm.weight_kg}
                                onChange={(e) => setProgressForm({...progressForm, weight_kg: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder={selectedClient.weight_kg?.toString() || ''}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-gray-300">Body Fat %</Label>
                              <Input
                                type="number"
                                value={progressForm.body_fat_percentage}
                                onChange={(e) => setProgressForm({...progressForm, body_fat_percentage: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder={selectedClient.body_fat_percentage?.toString() || ''}
                              />
                            </div>
                          </div>
                          
                          <div className="border-t border-border pt-4">
                            <Label className="text-gray-300 mb-2 block">Measurements (cm)</Label>
                            <div className="grid grid-cols-3 gap-3">
                              <Input
                                type="number"
                                value={progressForm.chest}
                                onChange={(e) => setProgressForm({...progressForm, chest: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder="Chest"
                              />
                              <Input
                                type="number"
                                value={progressForm.waist}
                                onChange={(e) => setProgressForm({...progressForm, waist: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder="Waist"
                              />
                              <Input
                                type="number"
                                value={progressForm.hips}
                                onChange={(e) => setProgressForm({...progressForm, hips: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder="Hips"
                              />
                              <Input
                                type="number"
                                value={progressForm.arms}
                                onChange={(e) => setProgressForm({...progressForm, arms: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder="Arms"
                              />
                              <Input
                                type="number"
                                value={progressForm.thighs}
                                onChange={(e) => setProgressForm({...progressForm, thighs: e.target.value})}
                                className="bg-secondary border-transparent"
                                placeholder="Thighs"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label className="text-gray-300">Notes</Label>
                            <Textarea
                              value={progressForm.notes}
                              onChange={(e) => setProgressForm({...progressForm, notes: e.target.value})}
                              className="bg-secondary border-transparent"
                              placeholder="Session notes, observations..."
                            />
                          </div>
                          
                          <Button
                            data-testid="submit-progress-btn"
                            onClick={logProgress}
                            disabled={submitting}
                            className="w-full bg-primary hover:bg-red-700"
                          >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Progress'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="border-border hover:border-primary">
                          <FileText className="w-4 h-4 mr-2" />
                          Add Note
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-card border-border">
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl text-white uppercase">
                            Add Client Note
                          </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <Textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className="bg-secondary border-transparent min-h-[120px]"
                            placeholder="Training notes, observations, recommendations..."
                          />
                          <Button
                            onClick={addNote}
                            disabled={submitting || !newNote.trim()}
                            className="w-full bg-primary hover:bg-red-700"
                          >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Note'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-border">
                  <div className="text-center">
                    <Scale className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <div className="font-mono text-xl text-white">{selectedClient.weight_kg || '-'}kg</div>
                    <div className="text-xs text-gray-500">Weight</div>
                  </div>
                  <div className="text-center">
                    <Target className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    <div className="font-mono text-xl text-white">{selectedClient.body_fat_percentage || '-'}%</div>
                    <div className="text-xs text-gray-500">Body Fat</div>
                  </div>
                  <div className="text-center">
                    <Flame className="w-5 h-5 text-primary mx-auto mb-1" />
                    <div className="font-mono text-xl text-white">{selectedClient.current_streak || 0}</div>
                    <div className="text-xs text-gray-500">Day Streak</div>
                  </div>
                  <div className="text-center">
                    <Trophy className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                    <div className="font-mono text-xl text-white">{selectedClient.total_points || 0}</div>
                    <div className="text-xs text-gray-500">Points</div>
                  </div>
                </div>
              </div>

              {/* Progress Charts */}
              <Tabs defaultValue="activity" className="w-full">
                <TabsList className="bg-secondary/50 border border-border">
                  <TabsTrigger value="activity" className="data-[state=active]:bg-primary">
                    <Activity className="w-4 h-4 mr-2" />
                    Activity
                  </TabsTrigger>
                  <TabsTrigger value="progress" className="data-[state=active]:bg-primary">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Progress
                  </TabsTrigger>
                  <TabsTrigger value="notes" className="data-[state=active]:bg-primary">
                    <FileText className="w-4 h-4 mr-2" />
                    Notes
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="activity" className="mt-4">
                  <div className="card-tactical">
                    <h4 className="font-heading text-lg text-white mb-4">Weekly Activity</h4>
                    {clientProgress?.activity_data?.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={clientProgress.activity_data.slice(-7)}>
                            <defs>
                              <linearGradient id="colorCalories" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#E31B23" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#E31B23" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                            <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '4px', color: '#fff' }} />
                            <Area type="monotone" dataKey="calories_burned" stroke="#E31B23" strokeWidth={2} fillOpacity={1} fill="url(#colorCalories)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No activity data yet</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Summary Stats */}
                    <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
                      <div className="text-center">
                        <div className="font-mono text-xl text-white">{clientProgress?.summary?.total_workouts || 0}</div>
                        <div className="text-xs text-gray-500">Workouts</div>
                      </div>
                      <div className="text-center">
                        <div className="font-mono text-xl text-white">{(clientProgress?.summary?.total_calories || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-500">Calories</div>
                      </div>
                      <div className="text-center">
                        <div className="font-mono text-xl text-white">{(clientProgress?.summary?.avg_daily_steps || 0).toLocaleString()}</div>
                        <div className="text-xs text-gray-500">Avg Steps</div>
                      </div>
                      <div className="text-center">
                        <div className="font-mono text-xl text-white">{clientProgress?.summary?.active_days || 0}</div>
                        <div className="text-xs text-gray-500">Active Days</div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="progress" className="mt-4">
                  <div className="card-tactical">
                    <h4 className="font-heading text-lg text-white mb-4">Body Composition Progress</h4>
                    {clientProgress?.progress_entries?.length > 0 ? (
                      <>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={clientProgress.progress_entries}>
                              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                              <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: '4px', color: '#fff' }} />
                              <Line type="monotone" dataKey="weight_kg" stroke="#E31B23" strokeWidth={2} dot={{ fill: '#E31B23' }} name="Weight (kg)" />
                              <Line type="monotone" dataKey="body_fat_percentage" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6' }} name="Body Fat %" />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                        
                        {/* Progress Entries List */}
                        <div className="mt-4 pt-4 border-t border-border">
                          <h5 className="text-sm text-gray-400 uppercase mb-3">Recent Entries</h5>
                          <div className="space-y-2">
                            {clientProgress.progress_entries.slice(-5).reverse().map((entry, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm">
                                <div className="flex items-center gap-3">
                                  <Calendar className="w-4 h-4 text-gray-400" />
                                  <span className="text-white">{entry.date}</span>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  {entry.weight_kg && <span className="text-gray-300">{entry.weight_kg}kg</span>}
                                  {entry.body_fat_percentage && <span className="text-gray-300">{entry.body_fat_percentage}% BF</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No progress entries yet</p>
                          <Button 
                            onClick={() => setShowProgressDialog(true)} 
                            className="mt-4 bg-primary hover:bg-red-700"
                          >
                            Log First Entry
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-4">
                  <div className="card-tactical">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-heading text-lg text-white">Training Notes</h4>
                      <Button 
                        onClick={() => setShowNoteDialog(true)}
                        variant="outline" 
                        size="sm"
                        className="border-border hover:border-primary"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Note
                      </Button>
                    </div>
                    
                    {clientNotes.length > 0 ? (
                      <ScrollArea className="h-[400px]">
                        <div className="space-y-3 pr-4">
                          {clientNotes.map((note) => (
                            <div key={note.id} className="p-4 bg-secondary/30 rounded-sm border-l-2 border-primary">
                              <p className="text-white">{note.note}</p>
                              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                <span>{note.trainer_name}</span>
                                <span>•</span>
                                <span>{new Date(note.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>No notes yet</p>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <div className="card-tactical h-[600px] flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a client to view details</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Performers */}
      {dashboard?.top_clients?.length > 0 && (
        <div className="card-tactical">
          <h3 className="font-heading text-xl text-white uppercase mb-4">Top Performers</h3>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {dashboard.top_clients.map((client, idx) => (
              <div 
                key={client.id}
                className="flex items-center gap-3 p-3 bg-secondary/30 rounded-sm"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  idx === 0 ? 'bg-yellow-500 text-black' :
                  idx === 1 ? 'bg-gray-400 text-black' :
                  idx === 2 ? 'bg-orange-600 text-white' :
                  'bg-secondary text-white'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium truncate">
                    {client.first_name} {client.last_name}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-primary">
                    <Flame className="w-3 h-3" />
                    {client.current_streak || 0} day streak
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
